import * as React from "react"
import {
  Card,
  CardHeader,
  CardContent,
  Stack,
  Typography,
  Divider,
  Switch,
  FormControlLabel,
  Box,
  LinearProgress,
} from "@mui/material"
import { api } from "../../function/axios"

export type ToolsSettings = {
  web_search: boolean
  file_search: boolean
  remote_mcp: boolean
}

export type ToolsSettingsFormProps = {
  value?: ToolsSettings
  defaultValues?: Partial<ToolsSettings>
  onChange?: (next: ToolsSettings) => void
  disabled?: boolean
  title?: React.ReactNode
  subtitle?: React.ReactNode
  className?: string
  sx?: any
}

export default function ToolsSettingsFormMUI({
  value,
  defaultValues,
  onChange,
  disabled,
  title = "Tools",
  subtitle = "Enable or disable available tools for the assistant",
  className,
  sx,
}: ToolsSettingsFormProps) {
  const [internal, setInternal] = React.useState<ToolsSettings>({
    web_search: defaultValues?.web_search ?? true,
    file_search: defaultValues?.file_search ?? true,
    remote_mcp: defaultValues?.remote_mcp ?? false,
  })
  const [loading, setLoading] = React.useState<boolean>(false)
  const [saving, setSaving] = React.useState<boolean>(false)
  const isExternallyControlled = value !== undefined
  const current = value ?? internal

  const fetchSettings = React.useCallback(async () => {
    setLoading(true)
    try {
      const res = await api({
        method: 'GET',
        url: '/ai-assistant/tools',
      })
      if (!res || res.code !== 200) throw new Error(`Fetch settings failed`)
      console.log("Result: ",res)
      const next: ToolsSettings = {
        web_search: !!res?.data?.web_search,
        file_search: !!res?.data?.file_search,
        remote_mcp: !!res?.data?.remote_mcp,
      }
      if (isExternallyControlled) {
        onChange?.(next)
      } else {
        setInternal(next)
        onChange?.(next)
      }
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }, [isExternallyControlled, onChange])

  const putSettings = React.useCallback(async (settings: ToolsSettings) => {
    setSaving(true)
    try {
      const res = await api({
        method: 'PUT',
        url: '/ai-assistant/tools',
        headers: { "Content-Type": "application/json" },
        body: {
          ...settings,
          vector_store_id: null,
        },
      })
      if (!res.code) throw new Error(`Set settings failed`)
    } catch (err) {
      console.error(err)
    } finally {
      setSaving(false)
    }
  }, [])

  React.useEffect(() => {
    fetchSettings()
  }, [fetchSettings])

  const update = (patch: Partial<ToolsSettings>) => {
    const next = { ...current, ...patch } as ToolsSettings
    if (isExternallyControlled) {
      onChange?.(next)
    } else {
      setInternal(next)
      onChange?.(next)
    }
    void putSettings(next)
  }

  const rows: Array<{ key: keyof ToolsSettings; title: string; desc: string }> = [
    { key: "web_search", title: "WEB Search:", desc: "Allow the assistant to browse the web when KB is insufficient." },
    { key: "file_search", title: "File Search:", desc: "Use the IRBIS Vector Store for company knowledge." },
    { key: "remote_mcp", title: "Remote MCP:", desc: "Optional remote tool calling (not wired by default)." },
  ]

  const controlsDisabled = !!disabled || loading || saving

  return (
    <Card
      variant="outlined"
      className={className}
      sx={{
        borderRadius: 2,
        borderColor: (t) => (t.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : "divider"),
        bgcolor: "background.paper",
        boxShadow: 0,
        ...sx,
      }}
    >
      <CardHeader
        title={<Typography variant="subtitle1" fontWeight={600}>{title}</Typography>}
        subheader={<Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
        sx={{ pb: 1.5 }}
      />
      {(loading || saving) && <LinearProgress />}
      <Divider />
      <CardContent sx={{ p: 0.5 }}>
        <Stack sx={{ px: 1, py: 3 }} gap={2}>
          {rows.map((r) => (
            <Stack
              key={r.key}
              direction="row"
              alignItems="flex-start"
              justifyContent="space-between"
              gap={2}
            >
              <Box>
                <Typography variant="body1">{r.title}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>{r.desc}</Typography>
              </Box>
              <FormControlLabel
                sx={{ m: 0 }}
                label=""
                control={
                  <Switch
                    checked={!!current[r.key]}
                    onChange={(e) => update({ [r.key]: e.target.checked } as Partial<ToolsSettings>)}
                    disabled={controlsDisabled}
                    inputProps={{ 'aria-label': r.title }}
                  />
                }
              />
            </Stack>
          ))}
        </Stack>
      </CardContent>
    </Card>
  )
}
